import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  MenuItem,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import RemoveShoppingCartOutlinedIcon from "@material-ui/icons/RemoveShoppingCartOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import { PageHeader, Section, StatCard, EmptyState } from "../../components/ui";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: 4,
    paddingBottom: 4,
    fontFamily: tk.font,
    ...theme.scrollbarStyles,
  },
  filterBar: {
    flex: "none",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  dateField: {
    flex: "0 0 160px",
    [theme.breakpoints.down("xs")]: { flex: "1 1 calc(50% - 6px)" },
  },
  selectField: {
    flex: "1 1 180px",
    maxWidth: 260,
    [theme.breakpoints.down("xs")]: { flex: "1 1 100%", maxWidth: "none" },
  },
  generate: {
    flex: "none",
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  section: {
    flex: "none",
    marginBottom: 16,
  },
  // 1 → 2 → 4 colunas; na linha de 4 a coluna nunca corta rótulo nem valor
  statGrid: {
    flex: "none",
    display: "grid",
    gap: 16,
    marginBottom: 16,
    gridTemplateColumns: "minmax(0, 1fr)",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (min-width: 1120px)": {
      gridTemplateColumns: "repeat(4, minmax(max-content, 1fr))",
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  tableScroll: {
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 650,
    // alinha a primeira/última coluna com o título da seção (20px)
    "& th:first-child, & td:first-child": { paddingLeft: 20 },
    "& th:last-child, & td:last-child": { paddingRight: 20 },
    "& tbody tr:last-child td": { borderBottom: 0 },
  },
}});

const RelatorioVendas = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: format(new Date().setDate(1), "yyyy-MM-dd"),
    dateTo: format(new Date(), "yyyy-MM-dd"),
    userId: "",
    motivoNaoVenda: "",
    motivoFinalizacao: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users);
    } catch (err) {
      toastError(err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGenerateReport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error("Por favor, selecione o período");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });

      if (filters.userId) {
        params.append("userId", filters.userId);
      }

      const { data } = await api.get(`/ticketreport/vendas?${params}`);
      setRelatorio(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Novo: filtrar tickets por motivo
  const filteredTickets = (relatorio?.tickets || []).filter((ticket) => {
    let motivoNaoVendaOk = true;
    let motivoFinalizacaoOk = true;
    if (filters.motivoNaoVenda) {
      motivoNaoVendaOk = ticket.motivoNaoVenda === filters.motivoNaoVenda;
    }
    if (filters.motivoFinalizacao) {
      motivoFinalizacaoOk =
        ticket.motivoFinalizacao === filters.motivoFinalizacao;
    }
    return motivoNaoVendaOk && motivoFinalizacaoOk;
  });

  const filterBar = (
    <div className={classes.filterBar}>
      <TextField
        className={classes.dateField}
        label="Data Inicial"
        type="date"
        value={filters.dateFrom}
        onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        className={classes.dateField}
        label="Data Final"
        type="date"
        value={filters.dateTo}
        onChange={(e) => handleFilterChange("dateTo", e.target.value)}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        select
        id="vendas-atendente"
        className={classes.selectField}
        label="Atendente"
        value={filters.userId}
        onChange={(e) => handleFilterChange("userId", e.target.value)}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">Todos</MenuItem>
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {user.name}
          </MenuItem>
        ))}
      </TextField>
      {relatorio && (
        <TextField
          select
          id="vendas-motivo-nao-venda"
          className={classes.selectField}
          label="Motivo da Não Venda"
          value={filters.motivoNaoVenda}
          onChange={(e) => handleFilterChange("motivoNaoVenda", e.target.value)}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">Todos</MenuItem>
          {(relatorio?.motivosNaoVenda || []).map((item, idx) => (
            <MenuItem key={idx} value={item.motivo}>
              {item.motivo}
            </MenuItem>
          ))}
        </TextField>
      )}
      {relatorio && (
        <TextField
          select
          id="vendas-motivo-finalizacao"
          className={classes.selectField}
          label="Motivo da Finalização"
          value={filters.motivoFinalizacao}
          onChange={(e) =>
            handleFilterChange("motivoFinalizacao", e.target.value)
          }
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">Todos</MenuItem>
          {(relatorio?.motivosFinalizacao || []).map((item, idx) => (
            <MenuItem key={idx} value={item.motivo}>
              {item.motivo}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleGenerateReport}
        disabled={loading}
        className={classes.generate}
        startIcon={<AssessmentOutlinedIcon />}
      >
        {loading ? "Gerando..." : "Gerar Relatório"}
      </Button>
    </div>
  );

  if (!relatorio) {
    return (
      <MainContainer maxWidth={1280}>
        <div className={classes.root}>
          <PageHeader
            title="Relatório de vendas"
            subtitle="Analise resultados, motivos de encerramento e desempenho da equipe."
          />
          {filterBar}
          <Section flush className={classes.section}>
            <EmptyState
              icon={<AssessmentOutlinedIcon />}
              title="Nenhum relatório gerado"
              message="Selecione o período e o atendente para gerar uma visão consolidada da operação comercial."
            />
          </Section>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer maxWidth={1280}>
      <div className={classes.root}>
        <PageHeader
          title="Relatório de vendas"
          subtitle="Analise resultados, motivos de encerramento e desempenho da equipe."
        />

        {filterBar}

        {/* Estatísticas Gerais */}
        <div className={classes.statGrid}>
          <StatCard
            label="Total de Vendas"
            value={relatorio.totalVendas}
            icon={<ShoppingCartOutlinedIcon />}
            tone="success"
          />
          <StatCard
            label="Valor Total de Vendas"
            value={formatCurrency(relatorio.totalValorVendas)}
            icon={<AttachMoneyIcon />}
            tone="brand"
          />
          <StatCard
            label="Não Vendas"
            value={relatorio.totalNaoVendas}
            icon={<RemoveShoppingCartOutlinedIcon />}
            tone="warning"
          />
          <StatCard
            label="Média de Tickets por Atendente"
            value={relatorio.mediaTicketPorAtendente.toFixed(1)}
            icon={<PeopleOutlineIcon />}
            tone="info"
          />
        </div>

        {/* Motivos de Não Venda */}
        {relatorio.motivosNaoVenda.length > 0 && (
          <Section title="Motivos de Não Venda" className={classes.section}>
            <div className={classes.chips}>
              {relatorio.motivosNaoVenda.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.motivo} (${item.quantidade})`}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Tabela detalhada de tickets */}
        {filteredTickets.length > 0 && (
          <Section flush title="Tickets Finalizados" className={classes.section}>
            <div className={classes.tableScroll}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Atendente</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Valor da Venda</TableCell>
                    <TableCell>Motivo da Não Venda</TableCell>
                    <TableCell>Motivo da Finalização</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell>{ticket.id}</TableCell>
                      <TableCell>{ticket.user?.name || "-"}</TableCell>
                      <TableCell>
                        {format(new Date(ticket.createdAt), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell align="right">
                        {ticket.valorVenda
                          ? formatCurrency(ticket.valorVenda)
                          : "-"}
                      </TableCell>
                      <TableCell>{ticket.motivoNaoVenda || "-"}</TableCell>
                      <TableCell>{ticket.motivoFinalizacao || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Section>
        )}

        {/* Tabela de Atendentes */}
        <Section flush title="Desempenho por Atendente" className={classes.section}>
          <div className={classes.tableScroll}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Atendente</TableCell>
                  <TableCell align="right">Total de Vendas</TableCell>
                  <TableCell align="right">Valor Total de Vendas</TableCell>
                  <TableCell align="right">Não Vendas</TableCell>
                  <TableCell align="right">Total de Tickets</TableCell>
                  <TableCell align="right">Média de Tickets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatorio.atendentes.map((atendente) => (
                  <TableRow key={atendente.id}>
                    <TableCell>{atendente.name}</TableCell>
                    <TableCell align="right">{atendente.totalVendas}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(atendente.totalValorVendas)}
                    </TableCell>
                    <TableCell align="right">{atendente.totalNaoVendas}</TableCell>
                    <TableCell align="right">{atendente.totalTickets}</TableCell>
                    <TableCell align="right">{atendente.mediaTicket}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>
    </MainContainer>
  );
};

export default RelatorioVendas;
